import exitIcon from '@jetbrains/icons/exit'
import pencilIcon from '@jetbrains/icons/pencil'
import spinnerIcon from '@jetbrains/icons/spinner.svg?react'
import {Directions} from '@jetbrains/ring-ui-built/components/popup/popup.consts'
import classNames from 'classnames'
import {memo, useState} from 'react'

import type {State} from '../../../reducers/types'
import {getBuildType, getBuildTypeLinks, getIsReadOnly, getProjectLinks} from '../../../selectors'
import type {ActiveEntityProps, TabParams} from '../../../types'
import {resolveWebEntityEditLink, resolveWebEntitySettingsLink} from '../../../utils/entityLinks'
import {buildTypeSettingsHint} from '../../packages/Hints/HintItems/Hints.buildOverview'
import {projectSettingsHint} from '../../packages/Hints/HintItems/Hints.projectOverview'
import {useHint} from '../../packages/Hints/Hints.hooks'
import {useSequenceLoaderReadyHandler} from '../SequenceLoader/SequenceLoader.hooks'
import type {SeqProps} from '../SequenceLoader/SequenceLoader.types'
import ToggleLink from '../ToggleLink/ToggleLink'
import Tooltip from '../Tooltip/Tooltip'

import styles from './EditEntity.module.css'

type OwnProps = SeqProps & {
  isGoBack?: boolean
}
type EditHref = {
  href: string | null | undefined
  noEditPermissions?: boolean
  readOnly?: boolean
}
type Props = OwnProps & ActiveEntityProps & EditHref

export function getEditHref(state: State, {buildTypeId, projectId}: TabParams): EditHref {
  const resolvedProjectId = projectId ?? getBuildType(state, buildTypeId)?.projectId
  const links = getBuildTypeLinks(state, buildTypeId) ?? getProjectLinks(state, projectId)
  const editHref = resolveWebEntityEditLink(links)
  return {
    href: editHref ?? resolveWebEntitySettingsLink(links),
    noEditPermissions: editHref == null,
    readOnly: getIsReadOnly(state, resolvedProjectId),
  }
}

function getIcon(loading: boolean, isGoBack: boolean) {
  if (loading) {
    return spinnerIcon
  }
  if (isGoBack) {
    return exitIcon
  }
  return pencilIcon
}

function EditEntity({
  href,
  noEditPermissions,
  readOnly = false,
  buildTypeId,
  isGoBack = false,
}: Props) {
  useSequenceLoaderReadyHandler(true)
  useHint(
    !isGoBack &&
      href != null &&
      (buildTypeId != null ? buildTypeSettingsHint : projectSettingsHint),
  )
  const [loading, setLoading] = useState(false)

  let title = 'Edit settings'
  if (isGoBack) {
    title = 'Exit Settings Mode'
  } else if (href == null) {
    title = 'No edit or view permissions'
  } else if (noEditPermissions) {
    title = 'View settings (no edit permissions)'
  } else if (readOnly) {
    title = 'View settings'
  }

  return (
    <Tooltip
      className={styles.tooltipWrapper}
      delay={100}
      title={title}
      popupProps={{offset: 8, directions: [Directions.BOTTOM_LEFT, Directions.BOTTOM_RIGHT]}}
    >
      <ToggleLink
        to={href ?? ''}
        disabled={href == null}
        icon={getIcon(loading, isGoBack)}
        switcherClassName={classNames({[styles.spinner]: loading})}
        label={isGoBack ? 'Exit settings' : 'Settings'}
        checked={isGoBack}
        {...(href != null
          ? {
              onConditionalClick: isPlainLeft => {
                if (isPlainLeft) {
                  setLoading(true)
                }
              },
            }
          : {})}
        data-hint-container-id={projectSettingsHint.id}
        loader={loading}
      />
    </Tooltip>
  )
}

export default memo(EditEntity)
